import React, { useState } from 'react';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, DataTable, EmptyState, ErrorState, FeedbackBanner, LoadingState, MvpCard, MvpTalentLayout, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const SecurityPage: React.FC = () => {
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<{ tokenId?: number; memberId?: number; mode: 'token' | 'member' } | null>(null);
  const { data, loading, error, reload } = useAsyncData(async (signal) => {
    const [sessions, events] = await Promise.all([
      platformCoreNetworkApi.listPortalSessions({ page: 1, limit: 20 }, { signal }),
      platformCoreNetworkApi.listSecurityEvents({ page: 1, limit: 20 }, { signal }),
    ]);
    return { sessions, events };
  }, []);

  return (
    <MvpTalentLayout title="Portal Security" subtitle="Session visibility, revocation actions, and recent security events.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading portal security data�" /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <MvpCard title="Portal sessions">
            {data.sessions.rows.length ? (
              <DataTable columns={['Token', 'Member', 'Created', 'Expires', 'Revoked', 'Last used', 'Actions']}>
                {data.sessions.rows.map((token) => (
                  <tr key={token.id}>
                    <td>#{token.id}</td>
                    <td>{token.memberId}</td>
                    <td>{formatDateTime(token.createdAt)}</td>
                    <td>{formatDateTime(token.expiresAt)}</td>
                    <td><StatusBadge status={token.revokedAt ? 'revoked' : 'active'} /></td>
                    <td>{formatDateTime(token.lastUsedAt)}</td>
                    <td>
                      <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setRevokeTarget({ tokenId: token.id, mode: 'token' })}>Revoke token</button>
                      <button type="button" className="mvp-button mvp-button--ghost" onClick={() => setRevokeTarget({ memberId: token.memberId, mode: 'member' })}>Revoke all sessions</button>
                    </td>
                  </tr>
                ))}
              </DataTable>
            ) : <EmptyState title="No session data" message="No portal sessions were returned by the platform API." />}
          </MvpCard>

          <MvpCard title="Security events">
            {data.events.rows.length ? (
              <DataTable columns={['Timestamp', 'Action', 'Actor', 'Target', 'Reason']}>
                {data.events.rows.map((event) => (
                  <tr key={event.id}>
                    <td>{formatDateTime(event.createdAt)}</td>
                    <td>{event.action}</td>
                    <td>{event.actorUser?.email || event.actorUserId}</td>
                    <td>{event.targetType} {event.targetId || ''}</td>
                    <td>{event.reason || '�'}</td>
                  </tr>
                ))}
              </DataTable>
            ) : <EmptyState title="No recent security activity" message="No security audit events were returned by the platform API." />}
          </MvpCard>
        </>
      ) : null}
      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={revokeTarget?.mode === 'member' ? 'Revoke all sessions for talent member' : 'Revoke portal token'}
        description="This is a sensitive security action and will be written to the global platform audit log."
        confirmLabel={revokeTarget?.mode === 'member' ? 'Revoke all sessions' : 'Revoke token'}
        confirmTone="danger"
        onClose={() => setRevokeTarget(null)}
        onConfirm={async ({ reason }) => {
          if (!revokeTarget) return;
          try {
            if (revokeTarget.mode === 'member' && revokeTarget.memberId) {
              await platformCoreNetworkApi.revokeAllMemberSessions(revokeTarget.memberId, reason);
            } else if (revokeTarget.tokenId) {
              await platformCoreNetworkApi.revokePortalToken(revokeTarget.tokenId, reason);
            }
            setFeedback({ tone: 'success', message: 'Security action completed.' });
            await reload();
          } catch (securityError: any) {
            setFeedback({ tone: 'error', message: securityError?.message || 'Unable to complete the security action.' });
            throw securityError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default SecurityPage;
